import { useMemo } from 'react';
import { useAppSelector } from 'flow/hooks/useRedux';
import { State } from 'flow/types/State';
import { TicketKey } from 'flow/types/EntityKeys';
import { SuggestedTagDTO } from 'flow/types/Tag';
import { selectTicketCallRecordKey } from 'flow/selectors/TicketEventSelectors';
import {
    selectExtendedTranscriptionIsPending,
    selectExtendedTranscriptionIsPersonal,
    selectExtendedTranscriptionKey,
} from 'flow/selectors/call-records-selectors';
import { useGetExtendedTranscription } from './useGetExtendedTranscription';

/**
 * Tags the extended transcription of a ticket's call already carries, in the shape the tags dialog
 * expects.
 *
 * The extended-transcription pipeline derives tags alongside the summary, so there's nothing to
 * generate: generateSuggestedTags builds its tags from the *legacy* transcript
 * (HomeOmniTicketServiceImplV2.generateTagsFromTranscriptOnly), which an extended-transcription call
 * doesn't have, so asking for generation fails. Prefer these and fall back to generation only when
 * there are none.
 *
 * `source` mirrors what the backend stamps on generated tags — the producing model
 * (OmniAiServiceImpl: setSource(defaultModel.getName())) — so tags added from here stay
 * distinguishable from manually added ones. `model` comes straight from the stored transcription
 * JSON, so it may be absent; the tag is then added without a source, exactly as before.
 *
 * Returns undefined when the ticket has no call, no extended transcription, or one carrying no tags.
 */
export const useTicketCallSuggestedTags = (
    ticketKey?: TicketKey,
): SuggestedTagDTO[] | undefined => {
    const callRecordKey = useAppSelector((state: State) =>
        ticketKey ? selectTicketCallRecordKey(state, ticketKey) : undefined,
    );
    const extendedTranscriptionKey = useAppSelector((state: State) =>
        callRecordKey
            ? selectExtendedTranscriptionKey(state, callRecordKey)
            : undefined,
    );
    const transcriptionIsPending = useAppSelector((state: State) =>
        callRecordKey
            ? selectExtendedTranscriptionIsPending(state, callRecordKey)
            : undefined,
    );
    const transcriptionIsPersonal = useAppSelector((state: State) =>
        callRecordKey
            ? selectExtendedTranscriptionIsPersonal(state, callRecordKey)
            : undefined,
    );

    // Normally a cache hit: the call card fetches this when the ticket feed renders.
    const { data: extendedTranscription } = useGetExtendedTranscription(
        extendedTranscriptionKey,
        transcriptionIsPending,
        transcriptionIsPersonal,
    );

    const tags = extendedTranscription?.tags;
    const model = extendedTranscription?.model;

    // Memoized: callers use this as an effect dependency, and mapping fresh objects on every render
    // would re-trigger them indefinitely.
    return useMemo(
        () =>
            tags?.length
                ? tags.map((label) => ({ label, source: model }))
                : undefined,
        [tags, model],
    );
};
